import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Tooltip, cn, type IconName } from "@sagot/ui";
import { useQuery } from "@tanstack/react-query";
import { consoleClient } from "../../app/api";
import { queryKeys } from "../../lib/query-keys";

export type OAuthProvider = "google" | "microsoft";
export type OAuthIntent = "login" | "signup" | "invite";

const PROVIDER_ICON: Record<OAuthProvider, IconName> = { google: "globe", microsoft: "app-window" };

export function oauthStartUrl(provider: OAuthProvider, intent: OAuthIntent, invite?: string): string {
  const params = new URLSearchParams({ intent });
  if (invite) params.set("invite", invite);
  return `${consoleClient.baseUrl}/v1/auth/oauth/${provider}/start?${params.toString()}`;
}

/**
 * Probe: the start endpoint redirects (302) when the provider is configured and answers
 * 404 `oauth_unavailable` while it is feature-flagged off (DEC-005).
 */
async function probe(provider: OAuthProvider): Promise<boolean> {
  try {
    const response = await fetch(oauthStartUrl(provider, "login"), { redirect: "manual", credentials: "include" });
    return response.type === "opaqueredirect" || (response.status >= 300 && response.status < 400);
  } catch {
    return false;
  }
}

export function useOAuthAvailability(provider: OAuthProvider): boolean {
  const query = useQuery({ queryKey: queryKeys.oauth(provider), queryFn: () => probe(provider), staleTime: Infinity, retry: false });
  return query.data === true;
}

interface OAuthButtonProps {
  provider: OAuthProvider;
  intent: OAuthIntent;
  label: string;
  invite?: string;
  className?: string;
}

/** Full-page redirect (no popups: FB in-app browser safe). Disabled with a tooltip until configured. */
export function OAuthButton({ provider, intent, label, invite, className }: OAuthButtonProps) {
  const { t } = useTranslation();
  const available = useOAuthAvailability(provider);
  const content = (
    <>
      <Icon name={PROVIDER_ICON[provider]} />
      {label}
    </>
  );
  if (available) {
    return (
      <Button asChild variant="secondary" className={cn("h-10", className)}>
        <a href={oauthStartUrl(provider, intent, invite)}>{content}</a>
      </Button>
    );
  }
  return (
    <Tooltip content={t("auth.common.notConfigured")}>
      <Button variant="secondary" aria-disabled="true" aria-describedby={undefined} className={cn("h-10 cursor-not-allowed opacity-60", className)}>
        {content}
        <span className="sr-only">({t("auth.common.notConfigured")})</span>
      </Button>
    </Tooltip>
  );
}
